/**
 * Enum representing appointment lifecycle statuses in the barbershop platform
 * Used to track and manage appointment states from booking to completion
 * @constant {object}
 * @property {string} SCHEDULED - Appointment is confirmed and scheduled for future date
 * @property {string} IN_PROGRESS - Appointment is currently active and service is being provided
 * @property {string} COMPLETED - Appointment has been finished successfully
 * @property {string} CANCELED - Appointment has been canceled by client or shop
 * @property {string} NO_SHOW - Client failed to show up for the scheduled appointment
 * @example
 * // Update appointment status
 * await updateAppointment(appointmentId, {
 *   status: APPOINTMENTS_STATUS.IN_PROGRESS,
 *   startedAt: new Date()
 * });
 *
 * // Filter completed appointments
 * const completedAppointments = appointments.filter(
 *   a => a.status === APPOINTMENTS_STATUS.COMPLETED
 * );
 */
export const APPOINTMENTS_STATUS = {
  /** Appointment is awaiting confirmation */
  PENDING: 'pending',
  /** Appointment is scheduled */
  SCHEDULED: 'scheduled',
  /** Appointment has been confirmed */
  CONFIRMED: 'confirmed',
  /** Client arrived for their appointment */
  CHECKED_IN: 'checked_in',
  /** Appointment is in progress */
  IN_PROGRESS: 'in_progress',
  /** Appointment is completed */
  COMPLETED: 'completed',
  /** Appointment is canceled */
  CANCELED: 'canceled',
  /** Alias for canceled status used by legacy payloads */
  CANCELLED: 'canceled',
  /** Appointment is marked as no show */
  NO_SHOW: 'no_show',
};

/**
 * Type representing valid appointment status values
 * @typedef {typeof APPOINTMENTS_STATUS[keyof typeof APPOINTMENTS_STATUS]} AppointmentsStatus
 */
export type AppointmentsStatus = (typeof APPOINTMENTS_STATUS)[keyof typeof APPOINTMENTS_STATUS];

/**
 * Enum representing member link statuses for shop-member relationships
 * Manages the lifecycle of professional relationships between shops and individual service providers
 * @constant {object}
 * @property {string} ACTIVE - Member link is currently active and operational
 * @property {string} BLOCKED - Member has been blocked from the shop
 * @property {string} PENDING - Membership request is awaiting approval
 * @property {string} APPROVED - Membership request has been approved by shop
 * @property {string} REJECTED - Membership request has been declined
 * @property {string} EXPIRED - Member link has expired due to time limit
 * @property {string} CANCELED_BYSHOP - Shop owner terminated the member relationship
 * @property {string} CANCELED_BYMEMBER - Individual member ended the relationship
 * @property {string} CANCELED_SYSTEM - System automatically canceled due to policy violation
 * @example
 * // Process member request
 * if (memberRequest.status === MEMBER_LINK_STATUS.PENDING) {
 *   await approveMemberRequest(memberRequest.id);
 * }
 *
 * // Get active shop members
 * const activeMembers = members.filter(
 *   m => m.linkStatus === MEMBER_LINK_STATUS.ACTIVE
 * );
 */
export const MEMBER_LINK_STATUS = {
  /** Member link is active */
  ACTIVE: 'active',
  /** Member relationship is retained but operationally paused */
  INACTIVE: 'inactive',
  /** Member link is blocked */
  BLOCKED: 'blocked',
  /** Member link is pending approval */
  PENDING: 'pending',
  /** Member link has been approved */
  APPROVED: 'approved',
  /** Member link has been rejected */
  REJECTED: 'rejected',
  /** Member link has expired */
  EXPIRED: 'expired',
  /** Member link has been canceled by the shop */
  CANCELED_BYSHOP: 'canceled_by_shop',
  /** Member link has been canceled by the member */
  CANCELED_BYMEMBER: 'canceled_by_member',
  /** Member link has been canceled by the system */
  CANCELED_SYSTEM: 'canceled_system',
};

/**
 * Type representing valid member link status values
 * @typedef {typeof MEMBER_LINK_STATUS[keyof typeof MEMBER_LINK_STATUS]} MemberLinkStatus
 */
export type MemberLinkStatus = (typeof MEMBER_LINK_STATUS)[keyof typeof MEMBER_LINK_STATUS];

/**
 * Enum representing booking statuses for chair/seat reservations
 * Tracks the lifecycle of workspace bookings within barbershops
 * @constant {object}
 * @property {string} PENDING - Booking request is awaiting confirmation
 * @property {string} CONFIRMED - Booking has been confirmed and reserved
 * @property {string} COMPLETED - Booking period has ended successfully
 * @property {string} CANCELED - Booking has been canceled before use
 * @property {string} NO_SHOW - Booked person failed to utilize the reserved time
 * @example
 * // Confirm pending booking
 * if (booking.status === BOOKING_STATUS.PENDING) {
 *   await confirmBooking(booking.id);
 * }
 *
 * // Track booking utilization
 * const utilizationRate = completedBookings.length / totalBookings.length;
 */
export const BOOKING_STATUS = {
  /** Booking is pending */
  PENDING: 'pending',
  /** Booking is confirmed */
  CONFIRMED: 'confirmed',
  /** Booking is completed */
  COMPLETED: 'completed',
  /** Booking is canceled */
  CANCELED: 'canceled',
  /** Booking is marked as no show */
  NO_SHOW: 'no_show',
};

/**
 * Type representing valid booking status values
 * @typedef {typeof BOOKING_STATUS[keyof typeof BOOKING_STATUS]} BookingStatus
 */
export type BookingStatus = (typeof BOOKING_STATUS)[keyof typeof BOOKING_STATUS];

/**
 * Enum representing payment transaction statuses
 * Tracks financial transactions for services, bookings, and subscriptions
 * @constant {object}
 * @property {string} PENDING - Payment is being processed or awaiting confirmation
 * @property {string} COMPLETED - Payment has been successfully processed and received
 * @property {string} FAILED - Payment processing failed due to insufficient funds or technical issues
 * @property {string} REFUNDED - Payment has been refunded to the customer
 * @property {string} CANCELED - Payment was canceled before processing
 * @example
 * // Process payment callback
 * switch (payment.status) {
 *   case PAYMENT_STATUS.COMPLETED:
 *     await fulfillOrder(payment.orderId);
 *     break;
 *   case PAYMENT_STATUS.FAILED:
 *     await notifyPaymentFailure(payment.userId);
 *     break;
 * }
 */
export const PAYMENT_STATUS = {
  /** Payment is pending */
  PENDING: 'pending',
  /** Payment is completed */
  COMPLETED: 'completed',
  /** Payment has failed */
  FAILED: 'failed',
  /** Payment has been refunded */
  REFUNDED: 'refunded',
  /** Payment is canceled */
  CANCELED: 'canceled',
};

/**
 * Type representing valid payment status values
 * @typedef {typeof PAYMENT_STATUS[keyof typeof PAYMENT_STATUS]} LegacyPaymentStatus
 */
export type LegacyPaymentStatus = (typeof PAYMENT_STATUS)[keyof typeof PAYMENT_STATUS];

/**
 * Enum representing user account statuses in the platform
 * Controls user access and account state management
 * @constant {object}
 * @property {string} ACTIVE - User account is active with full platform access
 * @property {string} INACTIVE - User account is temporarily inactive or dormant
 * @property {string} SUSPENDED - User account has been suspended due to policy violations
 * @property {string} DELETED - User account has been marked as deleted (soft delete)
 * @example
 * // Check user access before allowing operation
 * if (user.status !== USER_STATUS.ACTIVE) {
 *   throw new Error('User account is not active');
 * }
 *
 * // Filter active users for notifications
 * const activeUsers = users.filter(u => u.status === USER_STATUS.ACTIVE);
 */
export const USER_STATUS = {
  /** User is active */
  ACTIVE: 'active',
  /** User is inactive */
  INACTIVE: 'inactive',
  /** User is suspended */
  SUSPENDED: 'suspended',
  /** User is deleted */
  DELETED: 'deleted',
};

/**
 * Type representing valid user status values
 * @typedef {typeof USER_STATUS[keyof typeof USER_STATUS]} UserStatus
 */
export type UserStatus = (typeof USER_STATUS)[keyof typeof USER_STATUS];
